import {
  Box,
  type TextareaProps as CTextareaProps,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  type FormLabelProps,
  InputGroup,
} from "@chakra-ui/react";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  DiffSourceToggleWrapper,
  InsertImage,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  UndoRedo,
  diffSourcePlugin,
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from "@mdxeditor/editor";
import { forwardRef } from "react";
import "@mdxeditor/editor/style.css";

export interface EmailMarkdownInputProps extends Omit<CTextareaProps, "onChange"> {
  name: string;
  value?: any;
  helperText?: string;
  label: string;
  error?: string;
  labelProps?: FormLabelProps;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  defaultValue?: string;
  darkTheme?: boolean;
  onSubmit?: () => void;
}

export const EmailMarkdownInput = forwardRef<HTMLDivElement, EmailMarkdownInputProps>(
  (
    {
      name,
      value = "",
      label,
      helperText,
      isDisabled = false,
      isRequired = false,
      isInvalid = false,
      error,
      labelProps = {},
      mb = "2",
      defaultValue = "",
      darkTheme = false,
      onSubmit = () => {},
      ...rest
    },
    ref,
  ) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Check for Ctrl+Enter or Cmd+Enter
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
        onSubmit();
      }
    };

    return (
      <FormControl
        isDisabled={isDisabled}
        isRequired={isRequired}
        isInvalid={isInvalid}
        mt="1"
        mb={mb}
        size={rest?.size}
        onKeyDownCapture={handleKeyDown}>
        <FormLabel mt="0" p="0" fontSize="md" {...labelProps}>
          {label}
        </FormLabel>
        <InputGroup>
          <Box
            ref={ref}
            name={name}
            w="full"
            overflow="hidden"
            bg="white"
            borderWidth="1px"
            borderRadius="8"
            maxW="full"
            m="0"
            as={MDXEditor}
            readOnly={isDisabled}
            markdown={(value || defaultValue) ?? ""}
            className={darkTheme ? "dark-theme" : ""}
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              quotePlugin(),
              thematicBreakPlugin(),
              linkPlugin(),
              imagePlugin(),
              linkDialogPlugin(),
              diffSourcePlugin({
                diffMarkdown: defaultValue,
                viewMode: "rich-text",
                readOnlyDiff: true,
              }),
              toolbarPlugin({
                toolbarContents: () => (
                  <DiffSourceToggleWrapper>
                    <UndoRedo />
                    <BlockTypeSelect />
                    <BoldItalicUnderlineToggles />
                    <InsertThematicBreak />
                    <CreateLink />
                    <ListsToggle />
                    <InsertImage />
                  </DiffSourceToggleWrapper>
                ),
              }),
            ]}
            {...rest}
          />
        </InputGroup>
        <FormErrorMessage>{error}</FormErrorMessage>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);
